import Vue from "vue";
import { Component, Prop, Watch } from "vue-property-decorator";
import { IDownloadReportsModel } from "../../entities/download-reports-model";
import { bootbox } from "../../infrastructure/bootbox-shim";
import { FetchWrapper } from "../../infrastructure/fetch-wrapper";
import { IMultiSelectItem } from "../../infrastructure/multi-select-item";
import { ClientNotifier } from "../../scripts/ah.mb.client-notifications";
import { ReportServices } from "../../scripts/ah.mb.report-services";
import { modelServices } from "../../scripts/ah.mb.ui.model-services";
import { INodePathAndName } from "../../scripts/common/ah.mb.common.node-object";
import { DashboardServices, IDashboardExport, IDashboardPublishDefinition } from "../../scripts/dashboardServices";
import { EntityOverwriteOptions, IDashboardPublishOptions } from "../publish-to-dashboard-options/publish-to-dashboard-options";
import { IStringsEditorConfig } from "../strings-editor/strings-editor";

interface IValidationResult {
    errorMessage: string;
}

interface ICustomFieldDefs {
    customFieldDefs: Array<string>;
}


@Component
export default class PublishToDashboardDialog extends Vue {

    @Prop()
    value: INodePathAndName;

    definition: IDashboardPublishDefinition = { name: "", modelId: "", exports: [], setName: "", customFieldDefs: [] }

    definitionIsDirty: boolean = false;
    pristineDefinitionJson: string = "";

    @Prop()
    modelServices: modelServices;

    @Prop()
    reportServices: ReportServices;

    showPublishOptionsDialog: boolean = false;
    selectionToPublish: IDashboardExport[] = [];

    // Create convergence services
    dashboardServices = new DashboardServices();
    userNotifier = new ClientNotifier();

    modelNamesSelection: Array<IMultiSelectItem> = [];
    reportsSelection: Array<IMultiSelectItem> = [];

    showCustomFieldsEditor: boolean = false;
    customFieldsEditorOptions: IStringsEditorConfig = { allowAppend: true, allowDeleting: true, allowEditing: true, title: "", values: [] };
    showCustomFieldsEditorItem: ICustomFieldDefs;

    isValidNode: boolean = true;

    downloadReportsModel: IDownloadReportsModel =
        {
            mode: "byReportDefinition", modelId: "", createPivots: false, format: "excel", reportNodePathAndNames: [], singleSheet: false,
            reportNames: [],
            recursive: false
        }
    showDownloadReportsDialog: boolean = false;

    isBusy: boolean = false;


    mounted() {
        this.isValidNode = false;
        if (this.value.nodePathAndName)
            this.refreshNode();
    }

    onPublishCanceled() {

        this.showPublishOptionsDialog = false;

    }

    onPublishAccepted(options: IDashboardPublishOptions) {

        this.showPublishOptionsDialog = false;

        // userNotifier.showNotification("Validating model...", true, true);
        var notification = this.userNotifier.showBusyNotification("Publishing to dashboard...");

        // Todo: Ask if overwrite or update or what

        this.isBusy = true;

        if (this.selectionToPublish.length == 0) {
            // Publish all

            var request = {
                modelId: this.value.modelId,
                definitionNodePathAndNames: [this.value.nodePathAndName],
                overwriteOptions: options.overwriteOptions // EntityOverwriteOptions.overwriteIfAlreadyExists 
            }

            FetchWrapper.sendCommandAndHandle("/api/1.0/dashboard/publishbydefinition",
                {
                    method: "post", body: JSON.stringify(request)
                })
                .finally(() => {
                    this.userNotifier.clear(notification);
                    this.isBusy = false;
                })
        }
        else {
            // Selective publish

            this.selectionToPublish.forEach(value => {
                var request = {
                    modelId: this.value.modelId,
                    setName: this.definition.setName,
                    query: value.query,
                    nodePathAndNames: value.nodePathAndNames,
                    customFieldDefs: this.definition.customFieldDefs.concat(value.customFieldDefs),
                    overwriteOptions: options.overwriteOptions // EntityOverwriteOptions.overwriteIfAlreadyExists
                }

                FetchWrapper.sendCommandAndHandle("/api/1.0/dashboard/publishrecords",
                    {
                        method: "post", body: JSON.stringify(request)
                    })
                    .finally(() => {
                        this.userNotifier.clear(notification);
                        this.isBusy = false;
                    })

            })
        }
    }

    onPublishAllClick() {
        this.selectionToPublish = [];
        this.showPublishOptionsDialog = true;
    }


    onPublishSingleClick(item: IDashboardExport) {
        this.selectionToPublish = [item];
        this.showPublishOptionsDialog = true;

    }

    download(reportNames: string[]) {
        //this.isBusy = true;
        this.promptToSaveIfNeeded().then(() => {
            this.downloadReportsModel = {
                mode: "byRecordExportDefinition",
                modelId: this.value.modelId,
                singleSheet: false,
                format: "csv",
                reportNodePathAndNames: reportNames, // Leave empty for all exports 
                createPivots: false,
                reportNames: null, // Todo Fix this this.definition.exports.map(p => p.nodePathAndNames),
                //recordSetName: this.definition.setName,
                definitionNodePathAndName: this.value.nodePathAndName,
                recursive: false
            }
            this.showDownloadReportsDialog = true;
        });
    }


    onDownloadAllClick() {
        this.download([]);
    }

    onDownloadReportsDialogClosed() {
        this.showDownloadReportsDialog = false;
    }


    @Watch("definition", { deep: true })
    definitionChanged(oldVal: IDashboardPublishDefinition, newVal: IDashboardPublishDefinition) {

        if (!this.definitionIsDirty) {

            var currentDefJson = JSON.stringify(this.definition)
            if (this.pristineDefinitionJson !== currentDefJson)
                this.definitionIsDirty = true;
        }
    }

    promptToSaveIfNeeded(): Promise<boolean> {
        if (this.definitionIsDirty)
            return bootbox.confirm2(`Save changes to ${this.value.nodePathAndName}?`)
                .then(ok => {
                    if (ok) this.saveDefinition(this.value.nodePathAndName, this.definition);
                    return true;
                })
                .catch(() => { return true; })

        return Promise.resolve(true);
    }

    promptToSave(nodePathAndName: string, definition: IDashboardPublishDefinition): Promise<boolean> {
        return bootbox.confirm2(`Save changes to ${nodePathAndName}?`)
            //                .catch(() => reloadFunc())
            .then(ok => {
                if (ok) this.saveDefinition(nodePathAndName, definition);
                return true;
            })
            .finally(() => {
                this.refreshNode();
                return true;
            })

    }

    @Watch("value", { deep: true })
    recordsExportDefinitionNodeChanged(newVal: INodePathAndName, oldVal: INodePathAndName) {
        //console.log(newVal.nodePathAndName);

        if (this.definitionIsDirty)
            this.promptToSave(oldVal.nodePathAndName, this.definition)
        else
            this.refreshNode();
    }

    refreshNode() {
        // Todo: Display warning if unsaved changes

        var urlParams = new URLSearchParams();
        urlParams.set("modelId", this.value.modelId);
        urlParams.set("nodePathAndName", this.value.nodePathAndName);
        urlParams.set("variableName", "Definition");

        var url = `/api/1.0/query/getnodedatavalue?${urlParams.toString()}`;


        var reloadFunc = () => {
            FetchWrapper.fetch(url, { method: "get" })
                .then(response => response.json())
                .then(response => {
                    this.definition = response;
                    this.pristineDefinitionJson = JSON.stringify(this.definition);
                    // Only enable if not root object
                    this.isValidNode = this.value.nodePathAndName.lastIndexOf("\\") > 0;
                })
                .then(() => {

                    this.definitionIsDirty = false
                });
        };


        reloadFunc();

    }

    reportSearchFunction(query: string, done: (any) => void) {
        const searchUrl = this.modelServices.getNodeSearchUrl(this.value.modelId,
            {
                pathFilter: `\\Reports`,
                nodeTypes: ["Report"],
                //                recursive: true,
                searchPathAndName: query
                //               searchPathAndName: true
            });

        //const searchUrl = this.modelServices.getNodeSearchUrl(this.value.modelId, { searchPathAndName: query });
        FetchWrapper.fetch(searchUrl, { method: "get" })
            .then(res => res.json())
            .then((res: any) => {
                var namesOnly = res.map(p => p.name);
                done(namesOnly);
            });


    }

    nodeSearchFunction(query: string, done: (any) => void) {
        const searchUrl = this.modelServices.getNodeSearchUrl(this.value.modelId,
            {
                pathFilter: `\\Root`,
                nodeTypes: ["Sector"],
                //                recursive: true,
                searchPathAndName: query
                //               searchPathAndName: true
            });

        //const searchUrl = this.modelServices.getNodeSearchUrl(this.value.modelId, { searchPathAndName: query });
        FetchWrapper.fetch(searchUrl, { method: "get" })
            .then(res => res.json())
            .then((res: any) => {
                var namesOnly = res.map(p => p.name);
                done(namesOnly);
            });

    }

    addExportClick() {
        this.definition.exports.push({ nodePathAndNames: [], query: "", resultType: "Report Results", customFieldDefs: [] })
    }

    deleteExportClick(item: IDashboardExport) {
        var index = this.definition.exports.indexOf(item);
        this.definition.exports.splice(index, 1);
    }

    downloadExportClick(item: IDashboardExport) {
        this.download([item.query]);

        //this.downloadReportsModel = {
        //    mode: "byRecordStream",
        //    modelId: this.value.modelId,
        //    singleSheet: false,
        //    format: "excel",
        //    reportNodePathAndNames: [item.query],
        //    createPivots: false,
        //    reportNames: item.nodePathAndNames,
        //    recordSetName: this.definition.setName
        //}
        //this.showDownloadReportsDialog = true;

    }


    saveDefinition(nodePathAndName: string, definition: IDashboardPublishDefinition): Promise<any> {
        var request = {
            modelId: this.value.modelId,
            nodePathAndName: nodePathAndName,
            variableName: "Definition",
            value: definition,
        };

        return FetchWrapper.sendCommandAndHandle("/api/1.0/command/setnodedatavalue",
            {
                method: "post", body: JSON.stringify(request)
            })
            .then(response => {
                if (response.success) {
                    this.pristineDefinitionJson = JSON.stringify(this.definition);
                    this.definitionIsDirty = false;

                }
            })

    }

    //beforeRouteLeave(to, from, next) {

    //    if (this.definitionIsDirty) {
    //        this.promptToSave(this.value.nodePathAndName, this.definition)
    //            //                .then(() => next())
    //            .finally(() => next());
    //    }

    //    next();
    //}

    onSaveDefinitionClick() {
        this.saveDefinition(this.value.nodePathAndName, this.definition);
    }

    onAddNodeClick(item: IDashboardExport) {
        item.nodePathAndNames.push("");
    }

    onRemoveNodeClick(item: IDashboardExport, node: string) {
        var idx = item.nodePathAndNames.indexOf(node);
        if (idx >= 0)
            item.nodePathAndNames.splice(idx, 1);
    }

    onValidateClick() {
        var urlParams = new URLSearchParams();
        urlParams.set("modelId", this.value.modelId);
        urlParams.set("definitionNodePathAndName", this.value.nodePathAndName);

        var url = `/api/1.0/query/ValidateDashboardPublishDefinition?${urlParams.toString()}`;

        FetchWrapper.fetch(url, { method: "get" })
            .then(response => response.json())
            .then((response: IValidationResult[]) => {
                if (response.length > 0) {
                    var messages = response.map(p => "<p>" + p.errorMessage + "</p>");
                    var finalMessage = messages.join("");
                    bootbox.alertDialog("Validation", finalMessage);
                }
                else
                    bootbox.alertDialog("Validation", "No validation failures found");

            });
    }

    onDuplicateItemClick(item: IDashboardExport) {
        var itemJson = JSON.stringify(item);
        var newItem = JSON.parse(itemJson) as IDashboardExport;
        this.definition.exports.push(newItem);
    }

    onEditCustomFieldDefsClick() {
        this.showCustomFieldsEditorItem = this.definition;
        this.customFieldsEditorOptions = {
            allowAppend: true, allowDeleting: true, allowEditing: true, title: "Custom Field Definitions", values: this.definition.customFieldDefs
        };

        this.showCustomFieldsEditor = true;

    }

    onEditCustomFieldsDialogClosed(response: string, values: string[]) {

        this.showCustomFieldsEditor = false;
        if (response == "ok") {
            this.showCustomFieldsEditorItem.customFieldDefs = values;
            this.definitionIsDirty = true;
        }
    }

    onEditItemCustomFieldDefsClick(item: IDashboardExport) {
        this.showCustomFieldsEditorItem = item;
        this.customFieldsEditorOptions = {
            allowAppend: true, allowDeleting: true, allowEditing: true, title: `${item.query} Custom Field Definitions (+ ${this.definition.customFieldDefs.length} from parent)`,
            values: item.customFieldDefs,
        };

        this.showCustomFieldsEditor = true;

    }

}



// WEBPACK FOOTER //
// ./ClientApp/components/publish-to-dashboard-dialog/publish-to-dashboard-dialog.ts